/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
// React
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// lodash
import _ from 'lodash';

// React Spectrum
import {
  Divider,
  Flex,
  Grid,
  View,
} from '@adobe/react-spectrum';
import Ribbon from '@spectrum-icons/workflow/Ribbon';
// I18n
import { Translate } from 'react-redux-i18n';

// Framer Motion
import { motion } from 'framer-motion';
import { DetailTabAnimation } from '../../util/FramerMotionConfig';

// Styles
import '../../styles/ExpertiseTab.scss';
import '../../styles/TabCommon.scss';
import { CUSTOMER_DEPLOYMENTS } from '../../constants';
import ExpertiseContentCard from '../../components/Details/ExpertiseContentCard';
import { flexibleAddDummyCards, formatNumber, scrollToSection } from '../../util/util';
import SolutionBrief from '../../components/Details/SolutionBrief';

const ExpertiseTab = ({
  solutionExpertise, customerDeployments, specializations, companyInfo, totalCustomerDeploymentCount, accreditedSolutions, isMobile, programType,
}) => {
  const deploymentSection = useRef(null);

  const goToDeploymentSection = () => {
    if (deploymentSection && deploymentSection.current) {
      const headerOffset = 150;
      const elementPosition = deploymentSection.current?.getBoundingClientRect()?.top || 0;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (window.location.hash === '#expertise#certifications') scrollToSection('certification-section', isMobile, true);
    if (window.location.hash === '#expertise#specializations') scrollToSection('specialization-section', isMobile, true);
    if (window.location.hash === '#expertise#customerDeployments') scrollToSection('deployment-section', isMobile, true);
    if (window.location.hash === '#expertise#partnerSolutions') scrollToSection('solution-section', isMobile, true);
  }, [window.location.hash]);

  const renderSpecializationSection = () => {
    // If we have services
    if (_.size(specializations) > 0) {
      // Create the list of cards.
      const tempCards = specializations.map((card) => (
        <ExpertiseContentCard
          key={card.solution}
          header={card.solution}
          contentArray={card.region}
          tabSection="specializations"
          global={card.global}
          cardWidth="1"
        />
      ));
      const cards = flexibleAddDummyCards(tempCards, 'exp-content-card card-width-1');

      return (
        <div className="specialization-section">
          <h2><Translate value={`Specializations (${formatNumber(companyInfo.specializations)})`} /></h2>
          <h3>
            Specialized Partners demonstrate consistent customer success and have Adobe Certified Experts on staff to ensure successful customer implementations.
            {' '}
            <a href="https://solutionpartners.adobe.com/solution-partners/specialization.html" target="_blank" rel="noreferrer">Learn more</a>
            {' '}
            about specialized partners.
          </h3>
          <Flex gap="20px" wrap>
            {cards}
          </Flex>
        </div>
      );
    }
    return null;
  };

  const renderCertificationSection = () => {
    // If we have services
    if (_.size(solutionExpertise) > 0) {
      // Create the list of cards.
      const tempCards = solutionExpertise.map((card) => (
        <ExpertiseContentCard
          key={card.name}
          header={card.name}
          contentArray={[]}
          global={false}
          count={card.totalCertifications}
          tabSection="certifications"
          cardWidth="1"
        />
      ));
      const cards = flexibleAddDummyCards(tempCards, 'exp-content-card card-width-1');

      return (
        <div className="certification-section">
          {_.size(specializations) > 0 && <Divider size="M" marginTop={40} marginBottom={40} /> }
          <h2><Translate value={`Certifications (${formatNumber(companyInfo.certifications)})`} /></h2>
          <h3>Total number of certifications for all regions.</h3>
          <Flex gap="20px" wrap>
            {cards}
          </Flex>
        </div>
      );
    }
    return null;
  };

  const renderDeploymentsSection = () => {
    // If we have services
    if (_.size(customerDeployments) > 0) {
      // Create the list of cards.
      const tempCards = customerDeployments.map((card) => (
        <ExpertiseContentCard
          key={card.solutionName}
          header={card.solutionName}
          contentArray={[]}
          contentObject={card.region}
          global={false}
          count={card.totalCount}
          tabSection="deployment"
          cardWidth="1"
        />
      ));
      const cards = flexibleAddDummyCards(tempCards, 'exp-content-card card-width-1');
      return (
        <div className="deployment-section">
          {(_.size(specializations) > 0 || _.size(solutionExpertise) > 0) && <Divider size="M" marginTop={40} marginBottom={40} />}
          <h2><Translate value={`Customer Deployments (${formatNumber(totalCustomerDeploymentCount)})`} /></h2>
          <h3>Partner’s submitted customer deployment data from the last two years.</h3>
          <Flex gap="20px" wrap>
            {cards}
          </Flex>
        </div>
      );
    }
    return null;
  };

  const renderSolutinsSection = () => {
    if (_.size(accreditedSolutions) > 0) {
      return (
        <div className="solution-section">
          {(_.size(specializations) > 0 || _.size(solutionExpertise) > 0 || _.size(customerDeployments) > 0) && <Divider size="M" marginTop={40} marginBottom={40} />}
          <h2>{`Partner Solutions (${formatNumber(accreditedSolutions.length)})`}</h2>
          <h3><Translate value={programType === 'technology' ? 'partner_finder.detail.accredited_solutions_tab.tpp_accredited_solutions_description' : 'partner_finder.detail.accredited_solutions_tab.accredited_solutions_description'} /></h3>
          <div className="solution-brief-content">
            {accreditedSolutions.map((solution) => (
              <SolutionBrief accreditedSolution={solution} key={solution.headline} />
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={DetailTabAnimation.initial}
      animate={DetailTabAnimation.animate}
      exit={DetailTabAnimation.exit}
      transition={DetailTabAnimation.transition}
    >
      <Grid UNSAFE_className="tab-content-container">
        <View gridArea="tab-content" UNSAFE_className="tab-content">
          {renderSpecializationSection()}
          {renderCertificationSection()}
          {renderDeploymentsSection()}
          {renderSolutinsSection()}
        </View>
      </Grid>

    </motion.div>
  );
};

ExpertiseTab.propTypes = {
  solutionExpertise: PropTypes.arrayOf(PropTypes.shape({
    globalSpecialization: PropTypes.bool,
    name: PropTypes.string,
    regions: PropTypes.arrayOf(PropTypes.object),
    totalCertifications: PropTypes.number,
  })),
  customerDeployments: PropTypes.objectOf(PropTypes.array),
  specializations: PropTypes.arrayOf(PropTypes.object),
  companyInfo: PropTypes.shape({
    specializations: PropTypes.number,
    certifications: PropTypes.number,
  }),
  totalCustomerDeploymentCount: PropTypes.number,
  accreditedSolutions: PropTypes.arrayOf(PropTypes.shape({})),
};

ExpertiseTab.defaultProps = {
  solutionExpertise: [],
  customerDeployments: {},
  specializations: [],
  companyInfo: {},
  totalCustomerDeploymentCount: 0,
  accreditedSolutions: [],
};

export default ExpertiseTab;
